import React from "react";
import { Check } from "lucide-react";
import { money } from "@/lib/format";
import { useAuth } from "@/lib/auth";

export function StatsStrip({ stats }) {
  if (!stats) return null;
  const items = [
    { label: "Total contracted", value: money(stats.contracted), testId: "stat-contracted" },
    { label: "Collected", value: money(stats.collected), tone: "text-emerald-600 dark:text-emerald-400", testId: "stat-collected" },
    { label: "Ready to invoice", value: money(stats.ready_to_invoice), tone: "text-blue-600 dark:text-blue-400", testId: "stat-ready" },
    { label: "Awaiting approval", value: money(stats.awaiting), tone: "text-amber-700 dark:text-amber-400", sub: stats.overdue_count ? `${stats.overdue_count} overdue` : null, testId: "stat-awaiting" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border rounded-xl overflow-hidden" data-testid="stats-strip">
      {items.map((it) => (
        <div key={it.label} className="bg-card p-4 md:p-5">
          <div className="text-xs text-muted-foreground">{it.label}</div>
          <div className={`font-display text-xl md:text-2xl font-bold tabular-nums mt-1 ${it.tone || ""}`} data-testid={it.testId}>{it.value}</div>
          {it.sub && <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export function GettingStarted({ projects, goTo }) {
  const { user } = useAuth();
  const steps = [
    { label: "Add your company name", done: !!user?.company_name, action: () => goTo("settings") },
    { label: "Create your first project", done: projects.length > 0 },
    { label: "Send a milestone to your client", done: projects.some((p) => p.milestones.some((m) => m.sent_at)) },
    { label: "Get your first approval", done: projects.some((p) => p.milestones.some((m) => m.approved_at)) },
  ];
  if (steps.every((s) => s.done)) return null;
  const doneCount = steps.filter((s) => s.done).length;
  return (
    <div className="border border-border rounded-xl bg-card p-5" data-testid="getting-started">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold">Getting started</div>
        <div className="text-xs text-muted-foreground">{doneCount} of {steps.length}</div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {steps.map((s, i) => (
          <button key={s.label} onClick={s.action} disabled={!s.action || s.done} data-testid={`getting-started-step-${i}`}
            className={`flex items-center gap-2 text-left text-sm rounded-md border px-3 py-2 transition-colors ${s.done ? "border-emerald-500/40 text-muted-foreground line-through" : "border-border hover:border-indigo-500/50"}`}>
            <span className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${s.done ? "bg-emerald-600" : "border border-border"}`}>
              {s.done && <Check className="h-3 w-3 text-white" />}
            </span>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ProjectList({ projects, activeId, onSelect }) {
  if (projects.length === 0) return null;
  return (
    <div className="border border-border rounded-xl bg-card p-3" data-testid="project-list">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold px-2 py-2">Your projects ({projects.length})</div>
      <div className="space-y-1 max-h-[320px] overflow-y-auto sg-scroll">
        {projects.map((p) => {
          const done = p.milestones.filter((m) => ["paid", "ready_to_invoice"].includes(m.status)).length;
          const overdue = p.milestones.some((m) => m.status === "overdue");
          return (
            <button key={p.id} data-testid={`project-item-${p.id}`} onClick={() => onSelect(p.id)}
              className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors ${p.id === activeId ? "bg-indigo-600/10 ring-1 ring-indigo-500/40" : "hover:bg-accent"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium truncate">{p.title || p.client_name}</div>
                {overdue && <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />}
              </div>
              <div className="text-xs text-muted-foreground tabular-nums">{money(p.total_amount)} · {done}/{p.milestones.length} approved</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
